import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FaWhatsapp } from 'react-icons/fa'
import { HiOutlinePhone, HiOutlineMail, HiOutlineLogout } from 'react-icons/hi'
import brand, { whatsappLink } from '../brand'
import AuthenticationService from '../service/AuthenticationService'
import { allItemsLink } from '../util/shopFilters'
import { useIsStaff } from './RoleRoute'
import '../css/footer.css'

// Shopper browsing pages only. Product, cart and checkout have sticky bars; auth pages have their own layout.
const SHOPPER_PAGES = [/^\/$/, /^\/items\/all\/?$/, /^\/category\/[^/]+\/?$/, /^\/orders\/?$/, /^\/orders\/view\/[^/]+\/?$/, /^\/profile\/?$/]

// Admin and manager pages get the compact staff footer.
const STAFF_PAGES = [/^\/dashboard\/?$/, /^\/shop\/inventory(\/item)?\/?$/, /^\/shop\/items\/list\/[^/]+\/?$/, /^\/shop\/item\/create\/?$/,
    /^\/admin\/(orders|users|expenses)\/?$/, /^\/orders\/admin\/view\/[^/]+\/?$/, /^\/settings\/?$/, /^\/profile\/?$/]

const year = () => new Date().getFullYear()

const StaffFooter = () => {
    const canManageSettings = AuthenticationService.canManageSettings()
    const roleLabel = AuthenticationService.getRole() === 'MANAGER' ? 'Manager' : 'Admin'
    const userName = AuthenticationService.getAuthUserName()

    return (
        <footer className='mv-staff-footer'>
            <div className='mv-staff-footer-inner'>
                <div className='mv-staff-footer-who'>
                    <span className='mv-staff-footer-role'>{roleLabel}</span>
                    <span>{userName ? <>Signed in as <strong>{userName}</strong></> : `${brand.name} shop admin`}</span>
                </div>
                <nav className='mv-staff-footer-links' aria-label='Shop admin'>
                    <Link to='/dashboard'>Dashboard</Link>
                    <Link to='/shop/inventory'>Inventory</Link>
                    <Link to='/admin/orders'>Deliveries</Link>
                    <Link to='/admin/expenses'>Expenses</Link>
                    {canManageSettings && <Link to='/admin/coupons'>Coupons</Link>}
                    {canManageSettings && <Link to='/admin/users'>Users</Link>}
                    {canManageSettings && <Link to='/settings'>Settings</Link>}
                    <Link to='/profile'>Profile</Link>
                    <Link to='/logout' className='mv-staff-footer-out'><HiOutlineLogout aria-hidden='true' /> Log out</Link>
                </nav>
            </div>
            <div className='mv-staff-footer-bottom'>© {year()} {brand.fullName} · Shop admin</div>
        </footer>
    )
}

const ShopperFooter = () => {
    const contact = brand.contact || {}
    return (
        <footer className='mv-footer'>
            <div className='mv-footer-inner'>
                <div className='mv-footer-brand'>
                    <Link to='/' className={'mv-footer-logo' + (brand.logoCrop ? ' is-cropped' : '')} aria-label={brand.name + ' home'}>
                        <img src={brand.logo} alt={brand.fullName} loading='lazy' />
                    </Link>
                    <p>{brand.description}</p>
                </div>

                <nav className='mv-footer-col' aria-label='Shop'>
                    <h2>Shop</h2>
                    <Link to='/items/all'>All products</Link>
                    <Link to={allItemsLink({ for: 'girls' })}>Girls</Link>
                    <Link to={allItemsLink({ for: 'boys' })}>Boys</Link>
                    <Link to={allItemsLink({ age: '1' })}>Baby (0–1 yr)</Link>
                </nav>

                <nav className='mv-footer-col' aria-label='Your account'>
                    <h2>Your account</h2>
                    <Link to='/orders'>My orders</Link>
                    <Link to='/cart'>Cart</Link>
                    <Link to='/profile'>Profile</Link>
                </nav>

                <div className='mv-footer-col'>
                    <h2>Talk to us</h2>
                    {contact.whatsapp &&
                        <a className='mv-footer-wa' href={whatsappLink()} target='_blank' rel='noopener noreferrer'>
                            <FaWhatsapp aria-hidden='true' /> Chat on WhatsApp
                        </a>}
                    {contact.phoneDisplay &&
                        <a href={'tel:' + contact.phoneDisplay.replace(/[^+\d]/g, '')}>
                            <HiOutlinePhone aria-hidden='true' /> {contact.phoneDisplay}
                        </a>}
                    {contact.email &&
                        <a href={'mailto:' + contact.email}>
                            <HiOutlineMail aria-hidden='true' /> {contact.email}
                        </a>}
                </div>
            </div>
            <div className='mv-footer-bottom'>
                <span>© {year()} {brand.fullName}</span>
                <span>Made with love for little ones</span>
            </div>
        </footer>
    )
}

const SiteFooter = () => {
    const { pathname } = useLocation()
    const staff = useIsStaff()
    if (staff) return STAFF_PAGES.some(p => p.test(pathname)) ? <StaffFooter /> : null
    return SHOPPER_PAGES.some(p => p.test(pathname)) ? <ShopperFooter /> : null
}

export default SiteFooter
